import React, { useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDesktop,
  faMobileScreen,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import PrettyContainer from "../../prettyContainer/PrettyContainer";
import { getMyDevices, releaseMyDevices } from "../../../helpers/api";

// Paleta CardexApp
const AZUL = "#3790ed";
const AZUL_OSCURO = "#1c6fc4";
const MUTED = "#6b7a90";

export default function MyDevicesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const cargar = () => {
    setLoading(true);
    getMyDevices()
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch((msg) => {
        setError(typeof msg === "string" ? msg : "Could not load your devices.");
        setLoading(false);
      });
  };

  useEffect(() => {
    cargar();
  }, []);

  const liberar = () => {
    setError("");
    setSuccess("");
    setReleasing(true);
    releaseMyDevices()
      .then((res) => {
        setReleasing(false);
        setSuccess(res.message || "Your devices were released.");
        cargar();
      })
      .catch((res) => {
        setReleasing(false);
        const msg = res && res.message ? res.message : "Could not release your devices.";
        const cuando =
          res && res.nextReleaseAt
            ? ` You can release again on ${moment(res.nextReleaseAt).format("MMMM D, YYYY")}.`
            : "";
        setError(msg + cuando);
      });
  };

  const nombreDispositivo = (dev) => {
    const b = dev.browser || "Unknown browser";
    const o = dev.os || null;
    return o ? `${b} on ${o}` : b;
  };

  const esMovil = (dev) => /mobile|phone|tablet/i.test(dev.deviceType || "");

  const tarjetaResumen = (count, limit) => {
    const pct = limit > 0 ? Math.min(100, Math.round((count / limit) * 100)) : 0;
    const lleno = count >= limit;
    return (
      <div style={estilos.card}>
        <div style={estilos.resumenTop}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            <span style={{ fontSize: "2.1rem", fontWeight: 800, color: "#1d2735", lineHeight: 1 }}>
              {count}
            </span>
            <span style={{ color: MUTED, fontSize: "0.95rem" }}>of {limit} devices</span>
          </div>
          <span style={lleno ? estilos.pillFull : estilos.pillRoom}>
            {lleno ? "FULL" : "ROOM LEFT"}
          </span>
        </div>
        <div style={estilos.barTrack}>
          <div style={{ ...estilos.barFill, width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  const tarjetaDispositivo = (dev, i) => (
    <div key={dev.uuid || i} style={estilos.card}>
      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        <div style={estilos.iconBox}>
          <FontAwesomeIcon icon={esMovil(dev) ? faMobileScreen : faDesktop} color={AZUL_OSCURO} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: "#1d2735" }}>
            {nombreDispositivo(dev)}{" "}
            {dev.current && <span style={estilos.pillThis}>THIS ONE</span>}
          </div>
          <div style={{ color: MUTED, fontSize: "0.85rem", marginTop: "2px" }}>
            {[dev.city, dev.country].filter(Boolean).join(", ")}
            {[dev.city, dev.country].filter(Boolean).length ? " · " : ""}
            {dev.lastAccess ? moment(dev.lastAccess).fromNow() : "—"}
          </div>
        </div>
      </div>
    </div>
  );

  const cajaInfo = () => {
    const canRelease = data && data.canRelease;
    return (
      <div style={estilos.infoBox}>
        <FontAwesomeIcon icon={faCircleInfo} color={AZUL} style={{ marginTop: "3px" }} />
        <div>
          <div style={{ fontWeight: 700, color: "#1d2735" }}>
            You can release your devices once every {(data && data.cooldownDays) || 30} days
          </div>
          <div style={{ color: MUTED, fontSize: "0.9rem", marginTop: "2px" }}>
            {canRelease
              ? "Releasing signs out your other devices and frees a slot so you can sign in from a new one."
              : (
                <>
                  You already released recently. You can release again on{" "}
                  <strong>{moment(data && data.nextReleaseAt).format("MMMM D, YYYY")}</strong>.
                </>
              )}
          </div>
        </div>
      </div>
    );
  };

  const body = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "24px", color: MUTED }}>
          <Spinner animation="border" role="status" size="sm" /> Loading…
        </div>
      );
    }

    const devices = (data && data.devices) || [];
    const limit = (data && data.limit) || 2;
    const count = (data && data.count) || devices.length;
    const canRelease = data && data.canRelease;

    return (
      <div style={{ paddingTop: "0.6em" }}>
        {error && <Alert variant="warning">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {tarjetaResumen(count, limit)}

        {devices.length === 0 ? (
          <Alert variant="info">No devices are signed in right now.</Alert>
        ) : (
          devices.map((d, i) => tarjetaDispositivo(d, i))
        )}

        {cajaInfo()}

        <button
          onClick={liberar}
          disabled={!canRelease || releasing}
          style={{
            ...estilos.boton,
            opacity: !canRelease || releasing ? 0.5 : 1,
            cursor: !canRelease || releasing ? "not-allowed" : "pointer",
          }}
        >
          {releasing && (
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
          )}{" "}
          Release my devices
        </button>
      </div>
    );
  };

  return <PrettyContainer tittle="MY DEVICES">{body()}</PrettyContainer>;
}

const estilos = {
  card: {
    background: "#ffffff",
    border: "1px solid #e6ebf2",
    borderRadius: "16px",
    padding: "16px 18px",
    marginBottom: "12px",
    boxShadow: "0 1px 2px rgba(16,21,28,0.04)",
  },
  resumenTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  barTrack: {
    height: "8px",
    background: "#e6ebf2",
    borderRadius: "999px",
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    background: `linear-gradient(90deg, ${AZUL} 0%, ${AZUL_OSCURO} 100%)`,
    borderRadius: "999px",
    transition: "width .3s ease",
  },
  iconBox: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "#eef4fd",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: "1.05rem",
  },
  pillRoom: {
    background: "#e6f6ec",
    color: "#1a8a4a",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    padding: "5px 11px",
    borderRadius: "999px",
  },
  pillFull: {
    background: "#fdeaea",
    color: "#c0392b",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    padding: "5px 11px",
    borderRadius: "999px",
  },
  pillThis: {
    background: "#e8f1fd",
    color: AZUL_OSCURO,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    padding: "3px 9px",
    borderRadius: "999px",
    marginLeft: "6px",
    verticalAlign: "middle",
  },
  infoBox: {
    display: "flex",
    gap: "10px",
    background: "#eef4fd",
    borderLeft: `4px solid ${AZUL}`,
    borderRadius: "10px",
    padding: "14px 16px",
    marginTop: "6px",
    marginBottom: "16px",
  },
  boton: {
    background: `linear-gradient(135deg, ${AZUL} 0%, ${AZUL_OSCURO} 100%)`,
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    padding: "12px 22px",
    fontWeight: 700,
    fontSize: "0.95rem",
  },
};
